/**
 * Search index for the command palette.
 *
 * Kept separate from GlobalSearch.tsx so the matching rules can be tested
 * without rendering a dialog, and so the component stays about presentation.
 *
 * Everything here is pure. The caller supplies the page list (already security
 * -filtered by `useNavPalette`, from the navigation config) and the report catalog (lazy-loaded), so this
 * module never has to know how either is obtained.
 */

/** What a palette row can point at. */
export type PaletteKind = 'page' | 'report';

export interface PaletteItem {
  kind: PaletteKind;
  /** Stable key for React and for tests. */
  id: string;
  /** Primary line. */
  title: string;
  /** Secondary line — where it lives, or what it does. */
  subtitle?: string;
  /** Router path to navigate to on Enter. */
  route: string;
  /** Bootstrap icon class, without the leading `bi `. */
  icon: string;
}

/** Minimal shape this module needs from a nav item. */
export interface NavSource {
  label: string;
  icon: string;
  route?: string;
  /** Second line in the palette. Also searched, below the label. */
  subtitle?: string;
  /** Extra words to match on that are never shown. */
  keywords?: string;
}

/** Minimal shape this module needs from a report catalog entry. */
export interface ReportSource {
  description?: string;
  keywords?: string;
  group?: string;
}

// ---------------------------------------------------------------------------
// Display names
// ---------------------------------------------------------------------------

/**
 * Words that must not be title-cased letter-by-letter.
 *
 * Slugs are lowercase, so `aim-selective-menu-changes` would otherwise render
 * as "Aim Selective Menu Changes" — and "Aim" is not a word here, it is the
 * name of a subsystem. Users search for what the screen calls it.
 */
const ACRONYMS: Record<string, string> = {
  aim: 'AIM',
  bmi: 'BMI',
  dri: 'DRI',
  edi: 'EDI',
  ehr: 'EHR',
  emr: 'EMR',
  hl7: 'HL7',
  ibw: 'IBW',
  iddsi: 'IDDSI',
  kds: 'KDS',
  mds: 'MDS',
  pdpm: 'PDPM',
  pos: 'POS',
  rda: 'RDA',
  tpn: 'TPN',
  usda: 'USDA',
};

/**
 * Turns a report slug into something a person would recognise.
 *
 * The authoritative display names live in reportConfigs.ts, which is ~675 KB —
 * far too large to pull into the always-loaded bundle for the sake of a label.
 * Slugs are already human-authored and stable (see reportCatalog.ts), so
 * title-casing them lands very close, and the catalog's `description` carries
 * the detail on the second line.
 */
export function titleFromSlug(slug: string): string {
  return slug
    .split('-')
    .map((word) => ACRONYMS[word] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/**
 * Relevance score, or 0 for no match.
 *
 * Ranked so that what the user typed the beginning of comes first. Typing
 * "meal" should surface "Meal Patterns" above a report that merely mentions
 * meals in its keywords.
 */
export function scoreMatch(
  query: string,
  title: string,
  haystack = '',
): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;

  const t = title.toLowerCase();

  if (t === q) return 120;
  if (t.startsWith(q)) return 100;
  // A word boundary inside the title — "calorie" matching "Daily Calorie Count".
  if (t.split(/[\s/-]+/).some((word) => word.startsWith(q))) return 80;
  if (t.includes(q)) return 60;

  const h = haystack.toLowerCase();
  if (!h) return 0;
  if (h.split(/[\s/-]+/).some((word) => word.startsWith(q))) return 40;
  if (h.includes(q)) return 30;

  return 0;
}

// ---------------------------------------------------------------------------
// Index builders
// ---------------------------------------------------------------------------

/** Matching pages, best first. Input is expected to be security-filtered. */
export function searchPages(query: string, nav: readonly NavSource[]): PaletteItem[] {
  const scored: Array<{ score: number; item: PaletteItem }> = [];

  for (const entry of nav) {
    if (!entry.route) continue;
    const haystack = `${entry.subtitle ?? ''} ${entry.keywords ?? ''}`.trim();
    const score = scoreMatch(query, entry.label, haystack);
    if (score === 0) continue;

    scored.push({
      score,
      item: {
        kind: 'page',
        id: `page:${entry.route}`,
        title: entry.label,
        subtitle: entry.subtitle,
        route: entry.route,
        icon: entry.icon.replace(/^bi-/, ''),
      },
    });
  }

  return sortAndStrip(scored);
}

/**
 * Matching reports, best first.
 *
 * `isDisabled` filters out reports whose generator is switched off server-side.
 * Without it the palette would happily deep-link to a report that renders "not
 * available in web version" — a worse outcome than not finding it, because the
 * user has no way to tell a broken link from a broken feature.
 */
export function searchReports(
  query: string,
  catalog: Readonly<Record<string, ReportSource>>,
  isDisabled: (slug: string) => boolean,
): PaletteItem[] {
  const scored: Array<{ score: number; item: PaletteItem }> = [];

  for (const [slug, entry] of Object.entries(catalog)) {
    if (isDisabled(slug)) continue;

    const title = titleFromSlug(slug);
    const haystack = `${entry.description ?? ''} ${entry.keywords ?? ''}`;
    const score = scoreMatch(query, title, haystack);
    if (score === 0) continue;

    scored.push({
      score,
      item: {
        kind: 'report',
        id: `report:${slug}`,
        title,
        subtitle: entry.description,
        route: `/reports/run/${slug}`,
        icon: 'file-earmark-bar-graph',
      },
    });
  }

  return sortAndStrip(scored);
}

/** Ties break alphabetically so ordering is stable between renders. */
function sortAndStrip(
  scored: Array<{ score: number; item: PaletteItem }>,
): PaletteItem[] {
  return scored
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
    .map((entry) => entry.item);
}

/**
 * What the top bar search says it searches: "Search residents, items,
 * recipes". Only what this user can actually open is named (Items and Recipes
 * follow the role levels their pages need), and residents use the facility's
 * own word for them.
 */
export function globalSearchLabel(
  residentPluralLower: string,
  { items, recipes }: { items: boolean; recipes: boolean },
): string {
  const parts = [residentPluralLower];
  if (items) parts.push('items');
  if (recipes) parts.push('recipes');
  return `Search ${parts.join(', ')}`;
}
